(() => {
  'use strict';
  if (customElements.get('rc-calculator')) return;

  class RcCalculator extends HTMLElement {
    connectedCallback() {
      // A deferred asset normally upgrades parsed markup. A newly inserted editor
      // element can connect before its children exist, so initialise next frame.
      this.frame = requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.variantEvents?.abort();
    }

    init() {
      this.events?.abort();
      this.variantEvents?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      try {
        this.config = JSON.parse(this.querySelector('[data-calculator-json]').textContent);
        if (!this.config || !Array.isArray(this.config.variants) || !this.config.labels ||
            !this.config.schedule || typeof this.config.schedule !== 'object') return;
        this.number = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || undefined, {numberingSystem: 'latn', useGrouping: false, maximumFractionDigits: 3});
        // Validate the shop format before enabling controls.
        this.formatMoney(0);
      } catch (_) {
        // Preserve the server-rendered answer and disabled inputs on bad data.
        return;
      }
      this.variant = this.config.variants.find(v => v.id === this.config.variantId) || null;
      this.querySelector('[data-controls]').disabled = false;
      this.addEventListener('input', () => this.update(), {signal});
      this.addEventListener('change', () => this.update(), {signal});
      if (this.dataset.mode === 'product') {
        this.bindVariantPicker();
        customElements.whenDefined('product-variants').then(() => {
          if (this.isConnected && !signal.aborted) this.bindVariantPicker();
        });
      }
      this.update();
    }

    bindVariantPicker() {
      this.variantEvents?.abort();
      this.variantEvents = new AbortController();
      const path = url => {
        try { return new URL(url, document.baseURI).pathname; } catch (_) { return ''; }
      };
      const pickers = [...document.querySelectorAll('product-variants[data-main-product-page-variants]')]
        .filter(picker => path(picker.dataset.url) === path(this.dataset.productUrl));
      const sync = picker => {
        let selected = picker.currentVariant;
        if (selected === undefined) {
          try { selected = JSON.parse(picker.querySelector('[data-js-variant-data]').textContent); }
          catch (_) { return; }
        }
        this.variant = selected ? this.config.variants.find(v => v.id === String(selected.id)) || null : null;
        this.update();
      };
      pickers.forEach(picker => {
        // VARIANT_CHANGE is a plain, non-bubbling Event. Bind to its emitter.
        picker.addEventListener('VARIANT_CHANGE', () => sync(picker), {signal: this.variantEvents.signal});
      });
      if (pickers[0]) sync(pickers[0]);
    }

    plainText(html) {
      const template = document.createElement('template');
      template.innerHTML = html;
      return template.content.textContent;
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat;
      if (typeof window.Shopify?.formatMoney === 'function') {
        return this.plainText(window.Shopify.formatMoney(cents, format));
      }
      // Local's money helpers are private to shipping components. Use the same
      // Shopify placeholders when no public formatter is available.
      const formats = {
        amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_space_separator: [2, ' ', ','],
        amount_no_decimals_with_space_separator: [0, ' ', '.'],
        amount_with_period_and_space_separator: [2, ' ', '.'],
        amount_with_apostrophe_separator: [2, "'", '.']
      };
      const token = /\{\{\s*(\w+)\s*\}\}/;
      const parts = formats[format?.match(token)?.[1]];
      if (!parts) throw new Error('Unsupported shop money format');
      const [precision, thousands, decimal] = parts;
      const [whole, fraction] = (Math.round(cents) / 100).toFixed(precision).split('.');
      const amount = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) +
        (fraction ? decimal + fraction : '');
      return this.plainText(format.replace(token, amount));
    }

    choice(name) {
      return this.querySelector(`select[data-${name}], input[data-${name}]:checked`)?.value;
    }

    text(selector, value) {
      this.querySelectorAll(selector).forEach(node => { node.textContent = value; });
    }

    show(selector, visible) {
      this.querySelectorAll(selector).forEach(node => { node.hidden = !visible; });
    }

    productLabel(validPackage) {
      const title = this.config.productTitle;
      if (!validPackage) return title;
      const label = this.number.format(this.variant.grams / 1000) + ' ' + this.config.labels.kg;
      const normalize = text => text.toLowerCase().replace(/[\u00a0\u202f]/g, ' ');
      const match = normalize(label), compact = match.replaceAll(' ', '');
      const parts = normalize(title).split(match).join(compact).split(compact);
      // Match the whole formatted quantity: 120 kg must not count as 20 kg.
      const included = parts.slice(0, -1).some((before, i) =>
        !/[0-9.,]$/.test(before) && !/^[a-z0-9]/.test(parts[i + 1]));
      return title + (included ? '' : ' · ' + label);
    }

    update() {
      const config = this.config;
      const weight = this.querySelector('[data-weight]');
      const row = config.doseType === 'vaegt_alder_aktivitet' ? config.schedule?.[this.choice('age')]?.[this.choice('activity')]?.[weight.value] : null;
      const column = {normal: 0, ekstra: 1, maks: 2, maksimal: 2}[config.column];
      const grams = column === undefined ? null : row?.[column];
      const validDose = typeof grams === 'number' && Number.isFinite(grams) && grams > 0;
      const variant = this.variant;
      const validPackage = typeof variant?.grams === 'number' && Number.isFinite(variant.grams) && variant.grams > 0;
      const validPrice = !config.hidePrice && validPackage && Number.isFinite(variant.price) && variant.price > 0 &&
        Number.isFinite(config.vatFactor) && config.vatFactor >= 1 && config.vatFactor <= 1.3;

      const weightLabel = this.number.format(Number(weight.value)) + ' ' + config.labels.kg;
      this.text('[data-weight-output]', weightLabel);
      weight.setAttribute('aria-valuetext', weightLabel);
      this.text('[data-product-label]', this.productLabel(validPackage));
      this.text('[data-grams]', validDose ? this.number.format(grams) : config.labels.unknown);
      this.text('[data-scale-grams]', validDose ? this.number.format(grams) + ' ' + config.labels.g : config.labels.unknown);
      this.text('[data-scale-title]', config.labels.scale + ' ' + config.productTitle + ': ' +
        (validDose ? this.number.format(grams) + ' ' + config.labels.gramsPerDay : config.labels.unknown) + '.');
      this.show('[data-sold-out]', Boolean(variant && !variant.available));
      this.show('[data-error]', !validDose);
      this.text('[data-error]', config.labels.weightError);
      this.show('[data-days-line]', validDose && validPackage);
      this.text('[data-days]', validDose && validPackage ? this.number.format(Math.floor(variant.grams / grams)) : '');
      this.show('[data-price-line]', validDose && validPrice);
      this.show('[data-unit-line]', validPrice);
      const gross = validPrice ? variant.price * config.vatFactor : 0;
      this.text('[data-daily-price]', validDose && validPrice ? this.formatMoney(Math.round(gross * grams / variant.grams)) : '');
      this.text('[data-unit-price]', validPrice ? this.formatMoney(Math.round(gross * 1000 / variant.grams)) : '');
      this.text('[data-package-price]', validPrice ? ', ' + this.plainText(variant.formattedPrice) : '');
      this.text('[data-add-label]', variant?.available ? config.labels.add : config.labels.soldOut);
      this.querySelectorAll('[data-cart-variant]').forEach(input => { input.value = variant?.id || ''; });
      this.querySelectorAll('[data-add-to-cart]').forEach(button => { button.disabled = !variant?.available; });
    }
  }

  customElements.define('rc-calculator', RcCalculator);
  document.addEventListener('shopify:section:load', event => {
    // Also reconnect dosage widgets if the independently edited main product
    // section supplies a new picker. init() aborts previous listeners first.
    document.querySelectorAll('rc-calculator').forEach(element => {
      if (event.target.contains(element)) element.init();
      else if (element.config && element.dataset.mode === 'product') element.bindVariantPicker();
    });
  });
})();
